import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Radio, Disc, Users, Calendar, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REFRESH_MS = 60_000; // refresh once a minute

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const formatCountdown = (ms) => {
  if (ms <= 0) return 'starting now';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m`;
};

const StatusBadge = ({ party, now }) => {
  if (party.status === 'live') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-red-600 text-white px-2 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider">
        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Live now
      </span>
    );
  }
  if (party.status === 'post_chat') {
    return (
      <span className="inline-flex items-center gap-1 bg-[#FFDA33] text-black px-2 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider">
        Post-chat
      </span>
    );
  }
  if (party.status === 'ended') {
    return (
      <span className="inline-flex items-center gap-1 bg-[#1a1a1a] text-[#888] px-2 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider">
        Just ended
      </span>
    );
  }
  // scheduled
  const startMs = new Date(party.scheduled_start_at).getTime() - now;
  const soon = startMs <= 30 * 60 * 1000; // within 30 min
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider ${
      soon ? 'bg-[#FFDA33] text-black' : 'bg-[#0a0a0a] border border-[#FFDA33] text-[#FFDA33]'
    }`}>
      <Calendar size={9} /> {soon ? `Starts ${formatCountdown(startMs)}` : formatTime(party.scheduled_start_at)}
    </span>
  );
};

/**
 * Home-page strip showing imminent / live / recently-ended Listening Parties.
 * Visibility rules (server-side, scope=home):
 *   • status in [live, post_chat]
 *   • scheduled in the future (any time)
 *   • ended within the last 60 min
 * The section hides itself entirely when the API returns an empty list.
 */
export default function ListeningPartiesHomeStrip() {
  const [parties, setParties] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/listening-parties?scope=home&limit=8`);
        if (!alive) return;
        // Sort: live > post_chat > scheduled (soonest first) > ended (most recent first)
        const order = { live: 0, post_chat: 1, scheduled: 2, ended: 3 };
        const sorted = [...(res.data?.parties || [])].sort((a, b) => {
          const oa = order[a.status] ?? 4;
          const ob = order[b.status] ?? 4;
          if (oa !== ob) return oa - ob;
          if (a.status === 'scheduled') return new Date(a.scheduled_start_at) - new Date(b.scheduled_start_at);
          if (a.status === 'ended') return new Date(b.ended_at || 0) - new Date(a.ended_at || 0);
          return 0;
        });
        setParties(sorted.slice(0, 6));
      } catch {
        if (alive) setParties([]);
      } finally {
        if (alive) setLoaded(true);
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => { alive = false; clearInterval(t); clearInterval(tick); };
  }, []);

  if (!loaded || parties.length === 0) return null;

  const hasLive = parties.some((p) => p.status === 'live' || p.status === 'post_chat');

  return (
    <section className="px-4 md:px-8 py-8 border-t border-[#1a1a1a] bg-gradient-to-b from-[#FFDA33]/[0.04] to-transparent" data-testid="home-listening-parties-strip">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Radio size={16} className={hasLive ? 'text-red-500 animate-pulse' : 'text-[#FFDA33]'} />
              <span className="text-[#FFDA33] font-mono text-[10px] uppercase tracking-widest">
                // Listening Parties
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-['Chakra_Petch'] font-bold uppercase text-white tracking-tight">
              {hasLive ? 'A premiere is happening now' : 'Live album premieres on the way'}
            </h2>
          </div>
          <Link
            to="/listening-parties"
            className="text-[#FFDA33] hover:text-white font-mono text-xs uppercase tracking-wider flex items-center gap-1 transition-colors"
            data-testid="home-listening-parties-see-all"
          >
            See all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {parties.map((p) => (
            <Link
              key={p.id}
              to={`/listening-parties/${p.id}`}
              className="group block border border-[#222] hover:border-[#FFDA33] bg-[#0a0a0a] rounded-md overflow-hidden transition-colors"
              data-testid={`home-listening-party-card-${p.id}`}
            >
              <div className="aspect-square bg-[#111] relative overflow-hidden">
                {p.album_artwork ? (
                  <img
                    src={`${BACKEND_URL}/api/artwork/${p.album_artwork}`}
                    alt={p.album_title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Disc size={48} className="text-[#222]" />
                  </div>
                )}
                <div className="absolute top-2 left-2">
                  <StatusBadge party={p} now={now} />
                </div>
                {(p.status === 'live' || p.status === 'post_chat') && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/80 text-white px-1.5 py-0.5 rounded-sm text-[9px] font-mono">
                    <Users size={9} /> {p.participant_count}
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <h3 className="text-white font-['Chakra_Petch'] font-bold uppercase truncate text-xs sm:text-sm leading-tight">
                  {p.album_title}
                </h3>
                <p className="text-[#888] font-mono text-[10px] truncate mt-0.5">
                  @{p.host_username}
                </p>
                <p className="text-[#FFDA33] font-mono text-[10px] mt-1.5">
                  ${Number(p.event_price).toFixed(2)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
